/**
 * Карта — генерируемый SVG без графовых библиотек (AV-ADR-2, решение 6).
 *
 * Модуль чистый: на вход — уже разобранный контекст (`/videos/:id/context` или
 * `/collections/:id`), на выход — строка SVG. Раскладка считается в абсолютных
 * координатах, `viewBox` выводится из габаритов графа, поэтому карта одинаково
 * работает и для колоночного режима «В сериале», и для радиального «По темам».
 *
 * Свёртка больших сериалов — здесь же: раскрыт только текущий сезон, остальные
 * сезоны остаются компактными узлами со счётчиком серий. Сериал 10×10 рисует
 * 1 + 10 + 10 узлов, а не 100.
 */

import {
  episodeNumber,
  escapeAttr,
  escapeHtml,
  formatDuration,
  plural,
  type AvgVideoCollection,
  type AvgVideoSeason,
  type AvgVideoVideo,
} from './avg-video-shared';

export type AvgVideoMapMode = 'series' | 'topics';

export type MapNodeType = 'series' | 'season' | 'episode' | 'video' | 'collection' | 'more';

export interface MapNode {
  /** Ключ узла для кликов и подсветки: `${type}:${refId}`. */
  key: string;
  type: MapNodeType;
  refId: string;
  kicker: string;
  title: string;
  note: string;
  active: boolean;
  locked: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MapEdge {
  from: string;
  to: string;
  /** `h` — колоночная кривая слева направо, `arc` — дуга между произвольными узлами. */
  curve: 'h' | 'arc';
}

export interface MapGraph {
  nodes: MapNode[];
  edges: MapEdge[];
  ariaLabel: string;
  caption: string;
}

export interface SeriesMapInput {
  collection: AvgVideoCollection | null;
  seasons: AvgVideoSeason[];
  currentSeasonId: string | null;
  /** Серии ТОЛЬКО текущего сезона. */
  episodes: AvgVideoVideo[];
  currentVideoId: string | null;
}

export interface TopicsMapInput {
  video: AvgVideoVideo | null;
  topics: Array<{ collection: AvgVideoCollection; videos: AvgVideoVideo[] }>;
  selectedCollectionId: string | null;
}

const MAX_SEASONS = 12;
const MAX_EPISODES = 12;
const MAX_TOPICS = 8;
const MAX_TOPIC_VIDEOS = 8;

const GAP = 14;
const PAD = 30;

function episodesWord(n: number): string {
  return `${n} ${plural(n, 'серия', 'серии', 'серий')}`;
}

function videosWord(n: number): string {
  return `${n} ${plural(n, 'видео', 'видео', 'видео')}`;
}

function seasonKicker(season: AvgVideoSeason): string {
  return season.number ? `Сезон ${season.number}` : 'Сезон';
}

function seasonTitle(season: AvgVideoSeason): string {
  return season.title?.trim() || 'без названия';
}

/** Раскладывает колонку узлов по вертикали вокруг общего центра y = 0. */
function stackColumn(nodes: MapNode[], x: number): void {
  const total = nodes.reduce((sum, n) => sum + n.h, 0) + GAP * Math.max(0, nodes.length - 1);
  let cursor = -total / 2;
  for (const node of nodes) {
    node.x = x;
    node.y = cursor;
    cursor += node.h + GAP;
  }
}

// ============ Режим «В сериале» ============

export function buildSeriesGraph(input: SeriesMapInput): MapGraph {
  const nodes: MapNode[] = [];
  const edges: MapEdge[] = [];

  const title = input.collection?.title || 'Сериал';
  const totalEpisodes = input.collection?.video_ids.length || 0;

  const seriesNode: MapNode = {
    key: `series:${input.collection?.id || ''}`,
    type: 'series',
    refId: input.collection?.id || '',
    kicker: 'Сериал',
    title,
    note: totalEpisodes ? episodesWord(totalEpisodes) : '',
    active: !input.currentSeasonId && !input.currentVideoId,
    locked: false,
    x: 0,
    y: -40,
    w: 210,
    h: 80,
  };
  nodes.push(seriesNode);

  const seasons = input.seasons.slice(0, MAX_SEASONS);
  const hiddenSeasons = Math.max(0, input.seasons.length - seasons.length);
  const seasonNodes: MapNode[] = seasons.map((season) => {
    const isCurrent = season.id === input.currentSeasonId;
    return {
      key: `season:${season.id}`,
      type: 'season',
      refId: season.id,
      kicker: seasonKicker(season),
      // Развёрнут только текущий сезон; соседние остаются компактными счётчиками.
      title: isCurrent ? seasonTitle(season) : episodesWord(season.item_count || 0),
      note: isCurrent ? episodesWord(season.item_count || input.episodes.length) : '',
      active: isCurrent,
      locked: false,
      x: 0,
      y: 0,
      w: 196,
      h: isCurrent ? 74 : 56,
    };
  });

  if (hiddenSeasons > 0) {
    seasonNodes.push({
      key: `more:seasons:${input.collection?.id || ''}`,
      type: 'more',
      refId: input.collection?.id || '',
      kicker: 'Ещё сезоны',
      title: `+${hiddenSeasons}`,
      note: 'открыть список',
      active: false,
      locked: false,
      x: 0,
      y: 0,
      w: 196,
      h: 56,
    });
  }

  const episodes = input.episodes.slice(0, MAX_EPISODES);
  const hiddenEpisodes = Math.max(0, input.episodes.length - episodes.length);
  const episodeNodes: MapNode[] = episodes.map((video, idx) => ({
    key: `video:${video.id}`,
    type: 'episode',
    refId: video.id,
    kicker: `Серия ${episodeNumber(video, idx)}`,
    title: video.title,
    note: formatDuration(video.duration_seconds),
    active: video.id === input.currentVideoId,
    locked: video.access === 'locked',
    x: 0,
    y: 0,
    w: 224,
    h: 62,
  }));

  if (hiddenEpisodes > 0) {
    episodeNodes.push({
      key: `more:episodes:${input.currentSeasonId || input.collection?.id || ''}`,
      type: 'more',
      refId: input.collection?.id || '',
      kicker: 'Ещё серии',
      title: `+${hiddenEpisodes}`,
      note: 'открыть список',
      active: false,
      locked: false,
      x: 0,
      y: 0,
      w: 224,
      h: 56,
    });
  }

  const hasSeasons = seasonNodes.length > 0;
  if (hasSeasons) {
    stackColumn(seasonNodes, 270);
    stackColumn(episodeNodes, 540);
    nodes.push(...seasonNodes, ...episodeNodes);
    for (const season of seasonNodes) {
      edges.push({ from: seriesNode.key, to: season.key, curve: 'h' });
    }
    const currentSeasonNode = seasonNodes.find((n) => n.active);
    const parentKey = currentSeasonNode?.key || seriesNode.key;
    for (const episode of episodeNodes) {
      edges.push({ from: parentKey, to: episode.key, curve: 'h' });
    }
  } else {
    // Сериал без сезонов — валидный случай: серии висят прямо на сериале.
    stackColumn(episodeNodes, 270);
    nodes.push(...episodeNodes);
    for (const episode of episodeNodes) {
      edges.push({ from: seriesNode.key, to: episode.key, curve: 'h' });
    }
  }

  const seasonWord = input.seasons.length
    ? `${input.seasons.length} ${plural(input.seasons.length, 'сезон', 'сезона', 'сезонов')}`
    : 'без сезонов';

  return {
    nodes,
    edges,
    ariaLabel: `Карта сериала «${title}»: ${seasonWord}, раскрыт текущий сезон — в нём ${episodesWord(episodes.length)}.`,
    caption: `Сериал «${title}» · ${seasonWord}. Раскрыт только текущий сезон, остальные показаны счётчиками.`,
  };
}

// ============ Режим «По темам» ============

export function buildTopicsGraph(input: TopicsMapInput): MapGraph {
  const nodes: MapNode[] = [];
  const edges: MapEdge[] = [];

  const centerTitle = input.video?.title || 'Видео не выбрано';
  const centerNode: MapNode = {
    key: `video:${input.video?.id || ''}`,
    type: 'video',
    refId: input.video?.id || '',
    kicker: 'Видео',
    title: centerTitle,
    note: formatDuration(input.video?.duration_seconds),
    active: true,
    locked: input.video?.access === 'locked',
    x: -118,
    y: -40,
    w: 236,
    h: 80,
  };
  nodes.push(centerNode);

  const topics = input.topics.slice(0, MAX_TOPICS);
  const hiddenTopics = Math.max(0, input.topics.length - topics.length);
  const count = topics.length + (hiddenTopics > 0 ? 1 : 0);

  const RX = 350;
  const RY = 210;
  const angleOf = (index: number): number => (2 * Math.PI * index) / Math.max(1, count);

  const topicNodes: MapNode[] = topics.map((topic, idx) => {
    const angle = angleOf(idx);
    const w = 194;
    const h = 66;
    const size = topic.collection.video_ids.length || topic.videos.length;
    return {
      key: `collection:${topic.collection.id}`,
      type: 'collection',
      refId: topic.collection.id,
      kicker: 'Подборка',
      title: topic.collection.title,
      note: size ? videosWord(size) : '',
      active: topic.collection.id === input.selectedCollectionId,
      locked: false,
      x: Math.cos(angle) * RX - w / 2,
      y: Math.sin(angle) * RY - h / 2,
      w,
      h,
    };
  });

  if (hiddenTopics > 0) {
    const angle = angleOf(topics.length);
    const w = 194;
    const h = 56;
    topicNodes.push({
      key: `more:topics:${input.video?.id || ''}`,
      type: 'more',
      refId: input.video?.id || '',
      kicker: 'Ещё подборки',
      title: `+${hiddenTopics}`,
      note: 'открыть список',
      active: false,
      locked: false,
      x: Math.cos(angle) * RX - w / 2,
      y: Math.sin(angle) * RY - h / 2,
      w,
      h,
    });
  }

  nodes.push(...topicNodes);
  for (const topic of topicNodes) {
    edges.push({ from: centerNode.key, to: topic.key, curve: 'arc' });
  }

  // Выбранная подборка раскрывает остальные материалы темы вокруг себя.
  const selectedIdx = topics.findIndex((t) => t.collection.id === input.selectedCollectionId);
  if (selectedIdx >= 0) {
    const selected = topics[selectedIdx];
    const siblings = selected.videos
      .filter((v) => v.id !== input.video?.id)
      .slice(0, MAX_TOPIC_VIDEOS);
    const hiddenSiblings = Math.max(0, selected.videos.filter((v) => v.id !== input.video?.id).length - siblings.length);

    const baseAngle = angleOf(selectedIdx);
    // Веер держим узким: раскрытая тема не должна опоясывать карту и раздувать
    // её ширину так, что подписи узлов ужимаются до нечитаемых.
    const spread = Math.min(0.9, 0.22 * Math.max(1, siblings.length + (hiddenSiblings > 0 ? 1 : 0)));
    const outer: Array<{ key: string; node: MapNode }> = [];
    const shown: MapNode[] = siblings.map((video) => ({
      key: `video:${video.id}`,
      type: 'video',
      refId: video.id,
      kicker: 'Видео темы',
      title: video.title,
      note: formatDuration(video.duration_seconds),
      active: false,
      locked: video.access === 'locked',
      x: 0,
      y: 0,
      w: 210,
      h: 58,
    }));

    if (hiddenSiblings > 0) {
      shown.push({
        key: `more:collection:${selected.collection.id}`,
        type: 'more',
        refId: selected.collection.id,
        kicker: 'Ещё в теме',
        title: `+${hiddenSiblings}`,
        note: 'открыть подборку',
        active: false,
        locked: false,
        x: 0,
        y: 0,
        w: 210,
        h: 52,
      });
    }

    const RX2 = 620;
    const RY2 = 340;
    shown.forEach((node, idx) => {
      const t = shown.length === 1 ? 0 : (idx / (shown.length - 1)) * 2 - 1; // -1..1
      const angle = baseAngle + t * spread;
      node.x = Math.cos(angle) * RX2 - node.w / 2;
      node.y = Math.sin(angle) * RY2 - node.h / 2;
      outer.push({ key: node.key, node });
    });

    // Узел, уже нарисованный в другом месте карты, второй раз не добавляем.
    const existing = new Set(nodes.map((n) => n.key));
    for (const { key, node } of outer) {
      if (existing.has(key)) continue;
      existing.add(key);
      nodes.push(node);
      edges.push({ from: `collection:${selected.collection.id}`, to: key, curve: 'arc' });
    }
  }

  const topicsCount = input.topics.length;
  const ariaLabel = topicsCount
    ? `Карта тем видео «${centerTitle}»: ${topicsCount} ${plural(topicsCount, 'подборка', 'подборки', 'подборок')} вокруг выбранного видео.`
    : `Карта тем видео «${centerTitle}»: тематические подборки не назначены.`;

  return {
    nodes,
    edges,
    ariaLabel,
    caption: topicsCount
      ? 'Выбранное видео в центре, вокруг — его тематические подборки. Выберите подборку, чтобы раскрыть остальные материалы темы.'
      : 'У этого видео пока нет тематических подборок.',
  };
}

// ============ Рендер ============

interface Point { x: number; y: number }

function center(node: MapNode): Point {
  return { x: node.x + node.w / 2, y: node.y + node.h / 2 };
}

/** Точка на границе прямоугольника в направлении на другой узел. */
function anchor(node: MapNode, towards: Point): Point {
  const c = center(node);
  const dx = towards.x - c.x;
  const dy = towards.y - c.y;
  if (dx === 0 && dy === 0) return c;
  const hw = node.w / 2;
  const hh = node.h / 2;
  const scaleX = dx === 0 ? Infinity : hw / Math.abs(dx);
  const scaleY = dy === 0 ? Infinity : hh / Math.abs(dy);
  const t = Math.min(scaleX, scaleY);
  return { x: c.x + dx * t, y: c.y + dy * t };
}

function edgePath(from: MapNode, to: MapNode, curve: 'h' | 'arc'): string {
  if (curve === 'h') {
    const a = { x: from.x + from.w, y: from.y + from.h / 2 };
    const b = { x: to.x, y: to.y + to.h / 2 };
    const dx = Math.max(40, (b.x - a.x) * 0.55);
    return `M ${r(a.x)} ${r(a.y)} C ${r(a.x + dx)} ${r(a.y)}, ${r(b.x - dx)} ${r(b.y)}, ${r(b.x)} ${r(b.y)}`;
  }

  const a = anchor(from, center(to));
  const b = anchor(to, center(from));
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const px = (-dy / len) * len * 0.07;
  const py = (dx / len) * len * 0.07;
  return `M ${r(a.x)} ${r(a.y)} C ${r(a.x + dx * 0.35 + px)} ${r(a.y + dy * 0.35 + py)}, ${r(a.x + dx * 0.65 + px)} ${r(a.y + dy * 0.65 + py)}, ${r(b.x)} ${r(b.y)}`;
}

function r(n: number): string {
  return (Math.round(n * 10) / 10).toString();
}

function clip(text: string, width: number, fontSize: number): string {
  const max = Math.max(6, Math.floor((width - 24) / (fontSize * 0.56)));
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function nodeAriaLabel(node: MapNode): string {
  const parts = [node.kicker, node.title, node.note].filter((p) => p && p.trim());
  const label = parts.join(', ');
  return node.locked ? `${label}, по подписке` : label;
}

function renderNode(node: MapNode): string {
  const lines: Array<{ text: string; cls: string; size: number }> = [];
  if (node.kicker) lines.push({ text: node.kicker, cls: 'atv-map-kicker', size: 11 });
  if (node.title) lines.push({ text: node.title, cls: 'atv-map-name', size: 13.5 });
  if (node.note) lines.push({ text: node.note, cls: 'atv-map-note', size: 11 });

  const lineHeight = 17;
  const cx = node.x + node.w / 2;
  const firstY = node.y + node.h / 2 - ((lines.length - 1) * lineHeight) / 2;

  const texts = lines
    .map((line, idx) => `<text class="${line.cls}" x="${r(cx)}" y="${r(firstY + idx * lineHeight)}">${escapeHtml(clip(line.text, node.w, line.size))}</text>`)
    .join('');

  const classes = [
    'atv-map-node',
    `atv-map-node--${node.type}`,
    node.active ? 'is-active' : '',
    node.locked ? 'is-locked' : '',
  ].filter(Boolean).join(' ');

  return `<g class="${classes}" role="button" tabindex="0" aria-label="${escapeAttr(nodeAriaLabel(node))}" data-atv-map-node="${escapeAttr(node.key)}">`
    + `<title>${escapeHtml([node.kicker, node.title, node.note].filter(Boolean).join(' · '))}</title>`
    + `<rect x="${r(node.x)}" y="${r(node.y)}" width="${r(node.w)}" height="${r(node.h)}" rx="14"></rect>`
    + texts
    + '</g>';
}

/** Собирает SVG карты: viewBox выводится из габаритов графа, страницу не растягивает. */
export function renderMapSvg(graph: MapGraph): string {
  if (graph.nodes.length === 0) {
    return '<div class="atv-map-empty">Для этого материала карта пока пуста.</div>';
  }

  const byKey = new Map(graph.nodes.map((n) => [n.key, n]));
  const minX = Math.min(...graph.nodes.map((n) => n.x)) - PAD;
  const minY = Math.min(...graph.nodes.map((n) => n.y)) - PAD;
  const maxX = Math.max(...graph.nodes.map((n) => n.x + n.w)) + PAD;
  const maxY = Math.max(...graph.nodes.map((n) => n.y + n.h)) + PAD;
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);

  const edges = graph.edges
    .map((edge) => {
      const from = byKey.get(edge.from);
      const to = byKey.get(edge.to);
      if (!from || !to) return '';
      return `<path class="atv-map-edge" d="${edgePath(from, to, edge.curve)}"></path>`;
    })
    .filter(Boolean)
    .join('');

  const nodes = graph.nodes.map(renderNode).join('');

  /* Карта подстраивается под содержимое: своя ширина графа как потолок, своё
     соотношение сторон — и `meet`, который вписывает граф целиком. Дальше её
     ужимает CSS по высоте и ширине экрана, поэтому все узлы видны всегда и
     прокручивать карту не нужно. */
  const style = `aspect-ratio:${r(width)}/${r(height)};max-width:${Math.round(width)}px`;

  return `<svg class="atv-map-svg" viewBox="${r(minX)} ${r(minY)} ${r(width)} ${r(height)}" role="img"`
    + ` aria-label="${escapeAttr(graph.ariaLabel)}" preserveAspectRatio="xMidYMid meet" style="${style}">`
    + `<g class="atv-map-edges">${edges}</g>`
    + `<g class="atv-map-nodes">${nodes}</g>`
    + '</svg>';
}
